import { useQuery } from '@tanstack/react-query';
import {
  type FeeFilter,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import dayjs from 'dayjs';
import { feeKeys } from './keys';

const scheduledPayments = graphql(/* GraphQL */ `
  query fees_scheduledPayments($filter: FeeFilter) {
    fees_fees(filter: $filter) {
      paymentPlanInterval
      paymentPlanIntervalCount
      debtors {
        id
        feeId
        person {
          partyId
          firstName
          lastName
          avatarUrl
          type
          title {
            id
            name
            nameTextId
          }
        }
        classGroup {
          name
        }
        amount
        amountPaid
        amountDue
        installments {
          installment
          installmentDate
          installmentStatus
          amount
          serviceCharges
          absorbFees
          cardUsed
        }
      }
    }
  }
`);

const scheduledPaymentsQuery = (filter: FeeFilter) => ({
  queryKey: feeKeys.scheduledPayments(filter),
  queryFn: async () => {
    const data = await gqlClient.request(scheduledPayments, { filter });

    return {
      ...data,
      fees_fees: data.fees_fees.map((fee) => ({
        ...fee,
        debtors: (fee.debtors || [])
          .filter((debtor) => (debtor.installments || []).length > 0)
          .map((debtor) => ({
            ...debtor,
            installments: (debtor.installments || []).sort(
              (a, b) =>
                dayjs(a.installmentDate).unix() -
                dayjs(b.installmentDate).unix(),
            ),
            ...fee,
          })),
      })),
    };
  },
});

export function getFeeScheduledPayments(filter: FeeFilter) {
  return queryClient.fetchQuery(scheduledPaymentsQuery(filter));
}

export function useFeeScheduledPayments(filter: FeeFilter) {
  return useQuery({
    ...scheduledPaymentsQuery(filter),
    select: ({ fees_fees }) => (fees_fees.length ? fees_fees[0].debtors : []),
  });
}

export type ReturnTypeFromUseFeeScheduledPayments = UseQueryReturnType<
  typeof useFeeScheduledPayments
>[number];
